import { useState, type CSSProperties } from 'react'
import { MobileTabBar, mobileTabBarIcons } from '@/widgets/mobile-tab-bar'
import { PaymentHistoryPanel, type PaymentHistoryItem } from '@/widgets/payment-history'
import { Button, ContentGroup, ArrowLeftIcon, ChevronRightIcon } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import { routes, env } from '@/shared/config'
import type {
  DashboardSubscriptionShellVariant,
  DashboardSubscriptionUiStatus,
} from './dashboard-subscription-page-desktop'

type DashboardSubscriptionMobileProps = {
  className?: string
  shellVariant?: DashboardSubscriptionShellVariant
  status: DashboardSubscriptionUiStatus
  planTitle: string
  renewalText: string | null
  promoText: string | null
  paymentHistoryItems: PaymentHistoryItem[]
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.heading.h2),
  textAlign: 'center' as const,
}

const panelTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.body.mdSemibold),
}

const panelBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.base),
}

const promoBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.successStrong,
  ...typographyStyle(designTokens.typography.body.sm),
}

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: designTokens.colors.text.successStrong,
  borderRadius: designTokens.borders.radius.pill,
  padding: `${designTokens.spacing.xxs} ${designTokens.spacing.xs}`,
}

const badgeTextStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.subscription.statusLabel),
}

const navRowStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.body.mdSemibold),
}

const catalogTextStyle = <T extends CSSProperties>(style: T, isCatalogShell: boolean): T =>
  isCatalogShell ? ({ ...style, color: designTokens.colors.text.inverse } as T) : style

export function DashboardSubscriptionMobile({
  className,
  shellVariant = 'dashboard',
  status,
  planTitle,
  renewalText,
  promoText,
  paymentHistoryItems,
}: DashboardSubscriptionMobileProps) {
  const [showPaymentHistory, setShowPaymentHistory] = useState(false)
  const isCatalogShell = shellVariant === 'catalog'
  const mainGroupVariant = isCatalogShell ? 'planSelectorMobileCatalog' : 'planSelectorMobile'
  const panelGroupVariant = isCatalogShell
    ? 'transparentPanelMobileCatalog'
    : 'transparentPanelMobile'
  const navigationIconColor = isCatalogShell
    ? designTokens.colors.text.inverse
    : designTokens.colors.text.navigationChevron
  const headingIconColor = isCatalogShell
    ? designTokens.colors.text.inverse
    : designTokens.colors.text.primary

  return (
    <div className={cn('flex w-full flex-col lg:hidden', className)}>
      <div
        className="flex w-full flex-col"
        style={{ paddingBottom: designTokens.spacing.popupModalMobileBottomOffset }}
      >
        {showPaymentHistory ? (
          <ContentGroup variant={mainGroupVariant} className="w-full">
            <div className="flex w-full items-center" style={{ gap: designTokens.spacing.xxxl }}>
              <Button
                type="button"
                variant="tertiaryCancelIcon"
                aria-label="Назад"
                onClick={() => setShowPaymentHistory(false)}
              >
                <ArrowLeftIcon
                  width={designTokens.components.account.mobile.headerIconSize}
                  height={designTokens.components.account.mobile.headerIconSize}
                  color={headingIconColor}
                />
              </Button>
              <p style={catalogTextStyle(titleStyle, isCatalogShell)}>История платежей</p>
            </div>
            <PaymentHistoryPanel
              variant="mobile"
              tone={isCatalogShell ? 'catalog-dark' : 'default'}
              items={paymentHistoryItems}
              className="w-full"
            />
          </ContentGroup>
        ) : (
          <ContentGroup variant={mainGroupVariant} className="w-full">
            <div className="flex w-full items-center justify-center">
              <p style={catalogTextStyle(titleStyle, isCatalogShell)}>Моя подписка</p>
            </div>
            <ContentGroup variant={panelGroupVariant} className="w-full">
              <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.lg }}>
                {status === 'not_active' ? (
                  <>
                    <p style={catalogTextStyle(panelTitleStyle, isCatalogShell)}>
                      Активной подписки нет
                    </p>
                    <p style={catalogTextStyle(panelBodyStyle, isCatalogShell)}>
                      Материалы Epic Growth теперь доступны в Академии
                    </p>
                    <Button
                      asChild
                      variant="destructiveCancelRenewal"
                      className="w-full"
                      style={{ minHeight: designTokens.sizes.height.advertisingCardButton }}
                    >
                      <a href={env.academyPricingUrl} className="flex items-center justify-center">
                        Открыть Академию
                      </a>
                    </Button>
                  </>
                ) : (
                  <>
                    <div
                      className="flex flex-wrap items-baseline"
                      style={{ gap: designTokens.spacing.xs }}
                    >
                      <p style={catalogTextStyle(panelTitleStyle, isCatalogShell)}>{planTitle}</p>
                      <span style={badgeStyle}>
                        <span style={badgeTextStyle}>
                          {status === 'on_hold' ? 'Приостановлена' : 'активна'}
                        </span>
                      </span>
                    </div>
                    {renewalText ? (
                      <p style={catalogTextStyle(panelBodyStyle, isCatalogShell)}>{renewalText}</p>
                    ) : null}
                    {promoText ? (
                      <p style={catalogTextStyle(promoBodyStyle, isCatalogShell)}>{promoText}</p>
                    ) : null}
                  </>
                )}
              </div>
            </ContentGroup>
            {paymentHistoryItems.length > 0 ? (
              <button
                type="button"
                className="flex w-full items-center justify-between"
                style={{ minHeight: designTokens.spacing.xxxl }}
                onClick={() => setShowPaymentHistory(true)}
              >
                <span style={catalogTextStyle(navRowStyle, isCatalogShell)}>История платежей</span>
                <ChevronRightIcon
                  width={designTokens.components.account.mobile.navChevronSize}
                  height={designTokens.components.account.mobile.navChevronSize}
                  color={navigationIconColor}
                />
              </button>
            ) : null}
          </ContentGroup>
        )}
      </div>
      {shellVariant === 'dashboard' ? (
        <MobileTabBar
          className="lg:hidden"
          activeKey="subscription"
          items={[
            {
              key: 'home',
              label: 'Главная',
              icon: mobileTabBarIcons.home,
              href: routes.dashboardLk,
            },
            {
              key: 'subscription',
              label: 'Подписка',
              icon: mobileTabBarIcons.subscription,
              href: routes.dashboardSubscription,
            },
            {
              key: 'profile',
              label: 'Профиль',
              icon: mobileTabBarIcons.profile,
              href: routes.dashboardProfile,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

DashboardSubscriptionMobile.displayName = 'DashboardSubscriptionMobile'
